import React from "react";
import { ArrowUpRight } from "lucide-react";
import { TID } from "@/constants/coverifyIds";

const PERSONAS = [
  {
    tid: TID.personaAccountants,
    title: "Accountants",
    img: "https://images.unsplash.com/photo-1560250097-0b93528c311a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwxfHxhY2NvdW50YW50JTIwcHJvZmVzc2lvbmFsJTIwcG9ydHJhaXQlMjBvZmZpY2V8ZW58MHx8fHwxNzgwOTIwMzA1fDA&ixlib=rb-4.1.0&q=85",
    body:
      "Manage hundreds of client verifications without losing momentum. CoVerify gives accounting practices central tracking, automated workflows and inspection-ready records that scale with your AML obligations.",
    bullets: ["Bulk verification tracking", "Direct integration with CoSec App", "Inspection-ready audit trail"],
  },
  {
    tid: TID.personaCoSec,
    title: "Company Secretarial & ACSPs",
    img: "https://images.unsplash.com/photo-1590650153855-d9e808231d41?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwyfHxidXNpbmVzcyUyMHdvbWFuJTIwcG9ydHJhaXQlMjBvZmZpY2V8ZW58MHx8fHwxNzgwOTIwMzA1fDA&ixlib=rb-4.1.0&q=85",
    body:
      "Embed identity verification directly inside incorporation and appointment workflows. CoVerify keeps verified personal codes linked to the right client record, ready for Companies House.",
    bullets: ["ECCTA-aligned workflows", "Personal code retention", "Director & PSC verification"],
  },
  {
    tid: TID.personaLegal,
    title: "Corporate Law Practitioners",
    img: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMHdvbWFuJTIwcG9ydHJhaXQlMjBvZmZpY2V8ZW58MHx8fHwxNzgwOTIwMzA1fDA&ixlib=rb-4.1.0&q=85",
    body:
      "Built for legal teams that need rigorous KYC and clean audit chains across complex corporate structures. CoVerify produces signed reports your matter files can rely on.",
    bullets: ["Matter-level audit chains", "Beneficial owner verification", "Encrypted secure storage"],
  },
];

export default function Personas() {
  return (
    <section id="personas" className="py-20 md:py-28 bg-secondary/40 border-y border-border">
      <div className="container-cv">
        <div className="max-w-3xl">
          <span className="section-eyebrow">Built for the way you work</span>
          <h2 className="mt-4 font-display text-3xl md:text-5xl font-bold text-primary tracking-tight leading-[1.1]">
            Who CoVerify is for
          </h2>
          <p className="mt-4 text-base md:text-lg text-muted-foreground max-w-2xl">
            Whether you&apos;re verifying ten clients a quarter or thousands, CoVerify adapts to the compliance demands of your practice.
          </p>
        </div>

        <div className="mt-12 grid md:grid-cols-3 gap-6">
          {PERSONAS.map((p) => (
            <article
              key={p.title}
              data-testid={p.tid}
              className="group relative overflow-hidden rounded-2xl border border-border bg-white transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_24px_48px_-24px_rgba(14,58,95,0.25)]"
            >
              <div className="relative h-44 overflow-hidden">
                <img src={p.img} alt={p.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-primary/70 via-primary/10 to-transparent" />
                <div className="absolute top-3 left-3 h-1 w-12 bg-accent rounded-full" />
                <h3 className="absolute bottom-3 left-4 right-4 font-display text-xl font-bold text-white tracking-tight">
                  {p.title}
                </h3>
              </div>
              <div className="p-6">
                <p className="text-sm text-muted-foreground leading-relaxed">{p.body}</p>
                <ul className="mt-4 space-y-2">
                  {p.bullets.map((b, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-primary">
                      <span className="h-1.5 w-1.5 rounded-full bg-accent" />
                      <span className="font-medium">{b}</span>
                    </li>
                  ))}
                </ul>
                <a
                  href="/contact"
                  target="_blank"
                  rel="noreferrer"
                  className="mt-5 inline-flex items-center gap-1 text-sm font-bold text-primary group-hover:text-accent transition-colors"
                >
                  Learn more <ArrowUpRight className="h-4 w-4" />
                </a>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
